'use client';

import { useEffect, useState } from 'react';
import { type Locale } from '@/lib/i18n/locales';
import ErrorSection from '@/components/sections/utils/Error/Error';
import {
  errorMessages,
  type ErrorDictionary,
} from '@/content/i18n/error-messages';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
  params?: Promise<{ locale: string }> | { locale: string };
}

export default function Error({ error, reset, params }: ErrorProps) {
  const [locale, setLocale] = useState<Locale>('en');

  useEffect(() => {
    console.error(error);
  }, [error]);

  useEffect(() => {
    // Handle params which might be a Promise (Next.js 15) or undefined
    if (!params) {
      // Try to extract locale from URL as fallback
      const pathLocale = window.location.pathname.split('/')[1];
      if (pathLocale && ['en', 'es', 'uk'].includes(pathLocale)) {
        setLocale(pathLocale as Locale);
      }
      return;
    }

    // Handle Promise params (Next.js 15)
    if (params instanceof Promise) {
      params
        .then((resolvedParams) => {
          if (resolvedParams?.locale) {
            setLocale(resolvedParams.locale as Locale);
          }
        })
        .catch(() => {
          // Fallback to 'en'
          setLocale('en');
        });
    } else if (params.locale) {
      // Handle synchronous params
      setLocale(params.locale as Locale);
    }
  }, [params]);

  // Safely access the dictionary with fallback
  // Cast to unknown first to avoid "neither type sufficiently overlaps" error when casting to Record
  const dict: ErrorDictionary =
    (errorMessages as unknown as Record<string, ErrorDictionary>)[locale] ||
    errorMessages.en;

  return <ErrorSection dict={dict} reset={reset} />;
}
